import React, { useCallback, useEffect, useState } from 'react'
import { CAlert, CBadge, CButton, CCard, CCardBody, CCol, CContainer, CRow } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cibApple, cibGooglePlay } from '@coreui/icons'
import apiClient from '../../services/apiClient'
import { useNavigate } from 'react-router-dom'
import { API_VERSION } from '../../apiConfig'
import { buildApiUrl } from '../../utils/getConfiguration'
import { Browser } from '@capacitor/browser'
import { App } from '@capacitor/app'
import { Device } from '@capacitor/device'
import { SplashScreen } from '@capacitor/splash-screen'
import InternalAppHeader from './components/InternalAppHeader'
import { useTranslation } from 'react-i18next'
const About = () => {
  const [parameters, setParameters] = useState([])
  const [osVersion, setOsVersion] = useState('')
  const [platform, setPlatform] = useState('')
  const [appVersion, setAppVersion] = useState('')
  const [appBuild, setAppBuild] = useState('')
  const [storeVersion, setStoreVersion] = useState('')
  const [platformError, setPlatformError] = useState(null)

  const [isLoadingParameters, setIsLoadingParameters] = useState(false)
  const [parametersError, setParametersError] = useState(null)

  const { t } = useTranslation()

  const fetchParametersData = useCallback(async () => {
    try {
      setParametersError(null)
      setIsLoadingParameters(true)
      const response = await apiClient.get(buildApiUrl('appParameters'))
      setParameters(response.data)
    } catch (error) {
      console.error('Error fetching parameters', error)
      setParametersError(t('about.errors.parameters'))
    } finally {
      setIsLoadingParameters(false)
    }
  }, [t])

  useEffect(() => {
    fetchParametersData()
    const splash = document.getElementById('splash-screen')
    if (splash) splash.style.display = 'none'
    if (window?.SplashScreen?.hide) {
      SplashScreen.hide({ fadeOutDuration: 200 }).catch((splashError) => {
        console.warn('Unable to hide splash screen on About load', splashError)
      })
    }
  }, [fetchParametersData])

  useEffect(() => {
    ;(async () => {
      try {
        const d = await Device.getInfo()
        setPlatform(d.platform)
        setOsVersion(d.osVersion || '')

        const a = await App.getInfo()
        setAppVersion(a.version || '')
        setAppBuild(a.build || '')

        // VOLITELNÉ: zjištění nejnovější verze v App Store (iTunes Lookup API)
        // (funguje z nativu, z webu může bránit CORS)
        const { data: json } = await apiClient.get('https://itunes.apple.com/lookup?id=6475051196')
        setStoreVersion(json?.results?.[0]?.version || '')
      } catch (e) {
        console.error(e)
        setPlatformError(t('about.errors.platform'))
      }
    })()
  }, [t])
  const navigate = useNavigate()

  const iosStoreUrl = 'https://apps.apple.com/cz/app/reminder-ai/id6475051196?l=en'
  const androidStoreUrl = 'https://play.google.com/store/apps/details?id=com.aima.app'
  const appBuildSuffix = appBuild ? ` (${appBuild})` : ''
  const platformDisplay = platform || t('about.versionInfo.unknownPlatform')
  const osDisplay = osVersion || t('about.versionInfo.missingVersion')
  return (
    <div className="buying-list-wrapper min-vh-100 d-flex flex-column">
      <CContainer className="py-4 flex-grow-1">
        <InternalAppHeader title={t('about.header.title')} onBack={() => navigate('/dashboard')} />

        <CRow className="justify-content-center g-4 mt-2">
          <CCol lg={10}>
            <CCard className="border-0 shadow-sm">
              <CCardBody className="p-4 p-lg-5 d-flex flex-column gap-4">
                <section>
                  <h6 className="text-uppercase text-muted fw-semibold mb-3">
                    {t('about.sections.intro.title')}
                  </h6>
                  {isLoadingParameters && (
                    <CAlert color="info" className="mb-3">
                      {t('about.loading.parameters')}
                    </CAlert>
                  )}
                  {parametersError && !isLoadingParameters && (
                    <CAlert color="danger" className="mb-3">
                      {parametersError}
                    </CAlert>
                  )}
                  {!isLoadingParameters && !parametersError && parameters.length > 0 ? (
                    <div className="d-flex flex-column gap-3">
                      {parameters.map((parameter) => (
                        <div
                          key={parameter.id}
                          className="p-3 rounded border-start border-primary border-3 bg-light"
                        >
                          <pre className="mb-0 text-break" style={{ whiteSpace: 'pre-wrap' }}>
                            {parameter.hodnota}
                          </pre>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {!isLoadingParameters && !parametersError && parameters.length === 0 && (
                    <CAlert color="info" className="mb-0">
                      {t('about.messages.noReleaseNotes')}
                    </CAlert>
                  )}
                </section>

                <hr className="my-2" />

                <section>
                  <h6 className="text-uppercase text-muted fw-semibold mb-3">
                    {t('about.sections.store.title')}
                  </h6>
                  <div className="d-flex flex-wrap gap-3">
                    <CButton
                      type="button"
                      color="info"
                      component="a"
                      href={iosStoreUrl}
                      onClick={(e) => {
                        e.preventDefault()
                        if (Browser?.open) {
                          Browser.open({
                            url: iosStoreUrl,
                          })
                        } else {
                          window.open(iosStoreUrl, '_blank', 'noopener')
                        }
                      }}
                      title={t('about.storeButtons.ios.title')}
                      aria-label={t('about.storeButtons.ios.aria')}
                    >
                      <span className="d-inline-flex align-items-center gap-2">
                        <CIcon icon={cibApple} size="lg" /> {t('about.storeButtons.ios.label')}
                      </span>
                    </CButton>
                    <CButton
                      type="button"
                      color="info"
                      component="a"
                      href={androidStoreUrl}
                      onClick={(e) => {
                        e.preventDefault()
                        if (Browser?.open) {
                          Browser.open({
                            url: androidStoreUrl,
                          })
                        } else {
                          window.open(androidStoreUrl, '_blank', 'noopener')
                        }
                      }}
                      title={t('about.storeButtons.android.title')}
                      aria-label={t('about.storeButtons.android.aria')}
                    >
                      <span className="d-inline-flex align-items-center gap-2">
                        <CIcon icon={cibGooglePlay} size="lg" />{' '}
                        {t('about.storeButtons.android.label')}
                      </span>
                    </CButton>
                  </div>
                </section>

                <hr className="my-2" />

                <section>
                  <h6 className="text-uppercase text-muted fw-semibold mb-3">
                    {t('about.sections.versions.title')}
                  </h6>
                  <div className="d-flex flex-wrap align-items-center gap-3">
                    <span className="text-uppercase fw-semibold text-secondary">
                      {t('about.versionInfo.api', { version: API_VERSION })}
                    </span>
                    {appVersion && (
                      <CBadge color="info" className="px-3 py-2 text-uppercase">
                        {t('about.versionInfo.app', {
                          version: appVersion,
                          buildSuffix: appBuildSuffix,
                        })}
                      </CBadge>
                    )}
                    {storeVersion && (
                      <CBadge color="secondary" className="px-3 py-2 text-uppercase">
                        {t('about.versionInfo.store', { version: storeVersion })}
                      </CBadge>
                    )}
                  </div>
                  <CRow className="mt-3 row-cols-1 row-cols-md-2 g-3 text-muted small">
                    <CCol>
                      <strong>{t('about.versionInfo.platformLabel')}</strong> {platformDisplay}
                    </CCol>
                    <CCol>
                      <strong>{t('about.versionInfo.osLabel')}</strong> {osDisplay}
                    </CCol>
                  </CRow>
                  {platformError && (
                    <CAlert color="warning" className="mt-3 mb-0">
                      {platformError}
                    </CAlert>
                  )}
                </section>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  )
}
export default About
